import { SettingsPermissions } from "@redotech/account-common/permissions";
import { directMerchantAccess } from "../../settings-catalog/access";
import type {
  SettingGroupDefinition,
  SettingOwner,
  SettingRisk,
} from "../../settings-catalog/types";

export type InventoryGeneralFlatTeamSettingGroupId =
  | "inventory_general.reserved_inventory_buckets"
  | "inventory_general.cycle_count_variance"
  | "inventory_general.adjustment_reasons";

const groupOwner: SettingOwner = {
  kind: "teamSettings",
  path: "settings.ims",
  product: "fulfillment",
};

const groupRisk: SettingRisk = {
  level: "low",
  reason:
    "Inventory defaults for reserved stock, cycle-count review thresholds, and adjustment reasons.",
};

export const InventoryGeneralFlatTeamSettingGroups = [
  {
    id: "inventory_general.reserved_inventory_buckets",
    title: "Reserved inventory buckets",
    description:
      "The set of reserved inventory buckets your team can assign stock to, so reserved units are excluded from sellable inventory. Use this to create, rename, and remove the buckets themselves.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "inventory-management/general" },
    synonyms: ["reserved inventory buckets", "inventory general"],
  },
  {
    id: "inventory_general.cycle_count_variance",
    title: "Cycle count variance",
    description:
      "How far a cycle count may differ from expected on-hand before it has to be reviewed, as a percentage, a dollar value, or both. Turn a check off to let any variance through.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "inventory-management/general" },
    synonyms: ["cycle count variance", "inventory general"],
  },
  {
    id: "inventory_general.adjustment_reasons",
    title: "Adjustment reasons",
    description:
      "The reasons staff choose when adjusting inventory, such as damage, shrinkage, or write-offs. Add, rename, or remove the reasons offered in the adjustment flow.",
    productArea: "fulfillment",
    access: directMerchantAccess(
      SettingsPermissions.SET_ORDER_FULFILLMENT_SETTINGS,
    ),
    owner: groupOwner,
    risk: groupRisk,
    agentEditability: { mode: "requiresApproval" },
    fullPageTarget: { type: "settings", path: "inventory-management/general" },
    synonyms: ["adjustment reasons", "inventory general"],
  },
] as const satisfies readonly SettingGroupDefinition<InventoryGeneralFlatTeamSettingGroupId>[];

type _assertAllInventoryGeneralFlatTeamSettingGroupIdsProvided =
  InventoryGeneralFlatTeamSettingGroupId extends (typeof InventoryGeneralFlatTeamSettingGroups)[number]["id"]
    ? true
    : never;
const _allInventoryGeneralFlatTeamSettingGroupIdsProvided: _assertAllInventoryGeneralFlatTeamSettingGroupIdsProvided = true;
void _allInventoryGeneralFlatTeamSettingGroupIdsProvided;

export const InventoryGeneralFlatTeamSettingGroupById: Record<
  InventoryGeneralFlatTeamSettingGroupId,
  SettingGroupDefinition<InventoryGeneralFlatTeamSettingGroupId>
> = (() => {
  const map: Partial<
    Record<
      InventoryGeneralFlatTeamSettingGroupId,
      SettingGroupDefinition<InventoryGeneralFlatTeamSettingGroupId>
    >
  > = {};
  for (const group of InventoryGeneralFlatTeamSettingGroups) {
    map[group.id] = group;
  }
  return map as Record<
    InventoryGeneralFlatTeamSettingGroupId,
    SettingGroupDefinition<InventoryGeneralFlatTeamSettingGroupId>
  >;
})();
